<template>
  <div>
    <breadcrumb-list>
      <breadcrumb-link view="Home" label="Home" />
      <breadcrumb-link view="InquiryList" label="Inquiries" />
      <breadcrumb-link view="InquiryAsBlanketVOB" label="Create Blanket VOB" :active="true" />
    </breadcrumb-list>
    <section>
      <div class="container is-max-hd">
        <div class="columns">
          <div class="column is-two-thirds">
            <div class="box" style="position: relative">
              <div class="header-container">
                <h1 class="title">Create Blanket VOB search from Inquiry</h1>
              </div>

              <!-- Floating warning banner -->
              <FloatingWarningBanner
                :show="showWarningBanner"
                title="We're still processing your request"
                message="It may take longer to process. Feel free to leave this page and check Blanket VOB List shortly."
                @close="closeWarningBanner"
              />

              <hr />
              <form @submit.prevent="handleSubmit">
                <div class="block">
                  <div>
                    <AjaxSelectField
                      label="Select a provider"
                      placeholder="e.g. Healthcare Facility"
                      :options="providers"
                      :errors="errors.providerId"
                      v-model="form.providerId"
                      :loading="loadingProviders"
                    />
                    <AjaxSelectField
                      label="Select the Patient's state of residence"
                      placeholder="e.g. California"
                      :options="states"
                      :errors="errors.patientStateId"
                      v-model="form.patientStateId"
                      :loading="loadingStates"
                    />
                  </div>
                </div>

                <hr />

                <div class="columns">
                  <div class="column">
                    <b-field
                      label="Patient First Name"
                      :message="errors.patientFirstName"
                      :type="{ 'is-danger': errors.patientFirstName }"
                    >
                      <b-input
                        v-model="form.patientFirstName"
                        placeholder="Enter first name"
                        required
                      />
                    </b-field>
                  </div>
                  <div class="column">
                    <b-field
                      label="Patient Last Name"
                      :message="errors.patientLastName"
                      :type="{ 'is-danger': errors.patientLastName }"
                    >
                      <b-input
                        v-model="form.patientLastName"
                        placeholder="Enter last name"
                        required
                      />
                    </b-field>
                  </div>
                </div>

                <div class="columns">
                  <div class="column">
                    <b-field
                      label="Patient Date of Birth"
                      :message="errors.patientDOB"
                      :type="{ 'is-danger': errors.patientDOB }"
                    >
                      <b-datepicker
                        v-model="form.patientDOB"
                        :min-date="minDate"
                        :max-date="maxDate"
                        :date-parser="dateParser"
                        editable
                        locale="en-US"
                        placeholder="Click to select..."
                        icon="calendar-today"
                        :mobile-modal="false"
                        :mobile-native="false"
                        trap-focus
                        :use-html5-validation="false"
                        required
                      />
                    </b-field>
                  </div>
                  <div class="column">
                    <b-field
                      label="Patient SSN (Optional)"
                      :message="errors.patientSSN"
                      :type="{ 'is-danger': errors.patientSSN }"
                    >
                      <b-input
                        v-model="form.patientSSN"
                        placeholder="Enter SSN"
                        type="text"
                        maxlength="9"
                        inputmode="numeric"
                      />
                    </b-field>
                  </div>
                </div>

                <div class="columns is-vcentered">
                  <div class="column">
                    <div class="button-container">
                      <b-button
                        type="is-primary"
                        :disabled="submitting"
                        native-type="submit"
                        size="is-medium"
                        class="submit-button"
                      >
                        <span v-if="!submitting">Create Blanket VOB Search</span>
                        <span v-else class="loading-content">
                          <b-icon icon="loading" class="is-spinning loading-icon"></b-icon>
                          <span>Creating Blanket VOB Search...</span>
                        </span>
                      </b-button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
          <div class="column">
            <div class="box">
              <h2 class="title is-5">Instructions</h2>
              <div class="content">
                <p>Please fill in all the required information to create a new blanket VOB:</p>
                <ul>
                  <li>Select a provider from the dropdown list</li>
                  <li>Select the patient's state</li>
                  <li>Enter the patient's first and last name</li>
                  <li>Select the patient's date of birth</li>
                  <li>Optionally enter the patient's SSN (Social Security Number) XXX-XX-XXXX</li>
                </ul>
                <b-notification type="is-info is-light" :closable="false" class="mt-4">
                  Each Blanket Search counts as a transaction
                </b-notification>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import request from '../request';
import { format } from 'date-fns';
import AjaxSelectField from '../components/fields/AjaxSelectField.vue';
import FloatingWarningBanner from '../components/FloatingWarningBanner.vue';

export default {
  name: 'InquiryAsBlanketVOB',
  components: {
    AjaxSelectField,
    FloatingWarningBanner,
  },
  data() {
    return {
      form: {
        providerId: null,
        patientStateId: null,
        patientFirstName: '',
        patientLastName: '',
        patientDOB: null,
        patientSSN: '',
      },
      inquiry: null,
      providers: [],
      states: [],
      loadingProviders: true,
      loadingStates: true,
      submitting: false,
      errors: {},
      showWarningBanner: false,
      warningTimer: null,
    };
  },
  computed: {
    // Min date: 100 years ago
    minDate() {
      const date = new Date();
      date.setFullYear(date.getFullYear() - 100);
      return date;
    },
    // Max date: current date
    maxDate() {
      return new Date();
    },
  },
  async created() {
    await Promise.all([this.fetchProviders(), this.fetchStates()]);
    await this.fetchInquiryDetails();
  },
  methods: {
    dateParser(s) {
      // Change 01071981 -> 01/07/1981. Allows fast input without slashes.
      if (s.length === 8) {
        s = `${s.substr(0, 2)}/${s.substr(2, 2)}/${s.substr(4, 4)}`;
      }

      // Parse the date components
      const [month, day, year] = s.split('/').map((num) => parseInt(num, 10));

      // Create date at noon UTC to avoid timezone issues
      const parsedDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));

      const today = new Date();
      if (parsedDate > today || parsedDate < this.minDate) {
        return null;
      }
      return parsedDate;
    },
    async fetchProviders() {
      try {
        const response = await request('/providers/compact');
        this.providers = response.data.providers;
        this.providers = this.providers.filter((provider) => provider.is_active);
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to load providers',
          type: 'is-danger',
        });
      } finally {
        this.loadingProviders = false;
      }
    },
    async fetchStates() {
      try {
        const response = await request('/blanket-vob-search/patient-states');
        this.states = response.data.map((state) => ({
          id: state.id,
          name: state.name,
          abbreviation: state.abbreviation,
        }));
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to load states',
          type: 'is-danger',
        });
      } finally {
        this.loadingStates = false;
      }
    },

    async fetchInquiryDetails() {
      const inquiryId = this.$route.params.id;
      if (!inquiryId) {
        this.$buefy.toast.open({
          message: 'Inquiry ID is required',
          type: 'is-danger',
        });
        this.$router.push({ name: 'InquiryList' });
        return;
      }
      try {
        const response = await request(`/inquiries/${inquiryId}`);
        this.inquiry = response.data.inquiry;
        const { provider, coverage, submission } = this.inquiry;
        const patient = coverage?.patient || {};

        if (provider) {
          const match = this.providers.find((p) => p.id === provider.id);
          if (match) {
            this.form.providerId = match.id;
          } else {
            this.errors.providerId =
              'The provider used in this inquiry is no longer available. Please select another.';
            this.$buefy.toast.open({
              message: this.errors.providerId,
              type: 'is-warning',
            });
          }
        }

        this.form.patientFirstName = patient.first_name || '';
        this.form.patientLastName = patient.last_name || '';
        this.form.patientSSN = submission?.patientSSN || '';
        // Parse the ISO string directly into UTC components
        const [datePart] = patient.dob.split('T');
        const [year, month, day] = datePart.split('-').map(Number);
        const utcDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
        this.$set(this.form, 'patientDOB', utcDate);

        const foundState = this.states.find((s) => s.abbreviation === patient.address?.state);
        if (foundState) {
          this.form.patientStateId = foundState.id;
        } else {
          this.errors.patientStateId = `State "${patient.address?.state}" not found in available list. Please select a valid state.`;
          this.$buefy.toast.open({
            message: this.errors.patientStateId,
            type: 'is-warning',
          });
        }
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to load inquiry details',
          type: 'is-danger',
        });
      }
    },
    dateFormatter(date) {
      return format(date, 'MM/dd/yyyy');
    },
    validateForm() {
      const errors = {};
      // SSN validation: must be 9 digits if provided
      if (this.form.patientSSN && this.form.patientSSN.length !== 9) {
        //check if patientSSN is a number
        if (isNaN(this.form.patientSSN)) {
          errors.patientSSN = 'SSN must be a number';
        } else {
          errors.patientSSN = 'SSN must be exactly 9 digits';
        }
      }
      // Extend with more validations as needed
      return errors;
    },
    async handleSubmit() {
      this.submitting = true;
      this.errors = {};
      this.showWarningBanner = false;

      // Use the new validateForm method
      const validationErrors = this.validateForm();
      if (Object.keys(validationErrors).length > 0) {
        this.errors = validationErrors;
        this.submitting = false;
        return;
      }

      this.warningTimer = setTimeout(() => {
        this.showWarningBanner = true;
      }, 20000);

      try {
        await request('/blanket-vob-search', {
          method: 'POST',
          data: {
            ...this.form,
            patientDOB: this.form.patientDOB ? format(this.form.patientDOB, 'MM/dd/yyyy') : null,
          },
        });

        this.$buefy.toast.open({
          message: 'Blanket VOB search created successfully',
          type: 'is-success',
        });

        this.$router.push({
          name: 'BlanketVOBList',
        });
      } catch (error) {
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        } else {
          this.$buefy.toast.open({
            message: error.response?.data?.error || 'Failed to create blanket VOB search',
            type: 'is-danger',
          });
        }
      } finally {
        this.submitting = false;
        this.showWarningBanner = false;
        if (this.warningTimer) {
          clearTimeout(this.warningTimer);
          this.warningTimer = null;
        }
      }
    },
    closeWarningBanner() {
      this.showWarningBanner = false;
    },
  },
};
</script>

<style scoped>
.box {
  padding: 2rem;
}

.block {
  margin-bottom: 1.5rem;
}

.button-container {
  margin-top: 1.5rem;
  width: 100%;
  display: flex;
  justify-content: flex-start;
}

.submit-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.submit-button .loading-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  animation: fadeIn 0.3s ease-in;
}

.submit-button .loading-icon {
  font-size: 1rem;
  color: white;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(5px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Spinning animation for loading icon */
.is-spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

/* Header container for title */
.header-container {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
}

.header-container .title {
  margin-bottom: 0;
  flex-shrink: 0;
}

/* Override Buefy's built-in loading styles */
.submit-button.is-loading {
  pointer-events: none;
}

.submit-button.is-loading::after {
  display: none !important;
}
</style>
